import { isValidSlug } from '../shared/slug';

/**
 * Как приложение понимает, какую точку показывать (раздел 4 плана):
 * 1. хост `<slug>.<домен платформы>` или `<slug>.localhost` → slug из хоста, basename `/`;
 * 2. путь `/s/<slug>/...` на любом другом хосте (превью-деплои, тесты) → slug из пути;
 * 3. на боевом домене `/s/<slug>/...` → постоянный адрес на поддомене.
 * Сайт сети: `<сеть>/a/<точка>/...` — точка внутри сайта сети. Тогда slug и basename — точки,
 * а site — сам сайт (сеть): его slug и basename. У обычного адреса site нет (сайт = точка).
 */
export type SiteMode = 'subdomain' | 'path';

export type TenantLocation =
  | {
      kind: 'tenant';
      slug: string;
      basename: string;
      mode: SiteMode;
      /** Только для точки внутри сайта сети (`/a/<точка>/`); иначе сайт — сама точка. */
      site?: { slug: string; basename: string };
    }
  | { kind: 'redirect'; to: string }
  | { kind: 'platform' }
  | { kind: 'unknown'; reason: 'bad-slug' };

export type LocationInput = {
  hostname: string;
  pathname: string;
  search?: string;
  hash?: string;
  protocol?: string;
  port?: string;
};

const PATH_MODE = /^\/s\/([^/]+)(\/.*)?$/;
/** Точка внутри сайта сети: путь после basename сайта начинается с /a/<slug> (слэш в конце не обязателен). */
const CHILD = /^\/a\/([^/]+)(?:\/|$)/;

export function normalizeDomain(domain: string | undefined | null): string | null {
  const value = (domain ?? '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');
  return value.length > 0 ? value.replace(/^www\./, '') : null;
}

/** Сайт найден (slug, basename); если путь после basename — /a/<точка>/, это точка внутри сайта. */
function siteOrChild(
  siteSlug: string,
  siteBasename: string,
  rest: string,
  mode: SiteMode,
): Extract<TenantLocation, { kind: 'tenant' }> | { kind: 'unknown'; reason: 'bad-slug' } {
  const child = CHILD.exec(rest);
  if (!child?.[1]) return { kind: 'tenant', slug: siteSlug, basename: siteBasename, mode };
  if (!isValidSlug(child[1])) return { kind: 'unknown', reason: 'bad-slug' };
  return {
    kind: 'tenant',
    slug: child[1],
    basename: `${siteBasename}/a/${child[1]}`,
    mode,
    site: { slug: siteSlug, basename: siteBasename },
  };
}

function subdomainOf(hostname: string, base: string): string | null {
  const suffix = `.${base}`;
  if (!hostname.endsWith(suffix)) return null;
  return hostname.slice(0, -suffix.length);
}

export function resolveTenant(loc: LocationInput, platformDomainRaw: string | undefined | null): TenantLocation {
  const hostname = loc.hostname.toLowerCase();
  const platformDomain = normalizeDomain(platformDomainRaw);

  // 1. Поддомен: боевой домен или *.localhost в разработке.
  const sub = (platformDomain ? subdomainOf(hostname, platformDomain) : null) ?? subdomainOf(hostname, 'localhost');
  if (sub !== null) {
    if (sub === 'www') return { kind: 'platform' };
    if (sub.includes('.') || !isValidSlug(sub)) return { kind: 'unknown', reason: 'bad-slug' };
    return siteOrChild(sub, '', loc.pathname, 'subdomain');
  }

  const match = PATH_MODE.exec(loc.pathname);

  // 3. Боевой домен: у точки один адрес — поддомен.
  if (platformDomain && (hostname === platformDomain || hostname === `www.${platformDomain}`)) {
    if (match?.[1] && isValidSlug(match[1].toLowerCase())) {
      const protocol = loc.protocol ?? 'https:';
      const rest = match[2] ?? '/';
      return {
        kind: 'redirect',
        to: `${protocol}//${match[1].toLowerCase()}.${platformDomain}${rest}${loc.search ?? ''}${loc.hash ?? ''}`,
      };
    }
    return { kind: 'platform' };
  }

  // 2. Любой другой хост: превью-деплой, localhost, тесты — адрес /s/<slug>/.
  if (match?.[1]) {
    const slug = match[1];
    if (!isValidSlug(slug)) return { kind: 'unknown', reason: 'bad-slug' };
    return siteOrChild(slug, `/s/${slug}`, match[2] ?? '/', 'path');
  }
  return { kind: 'platform' };
}

/** Абсолютный адрес точки: поддомен на боевом домене, иначе путь /s/<slug>/ на текущем хосте. */
export function tenantBaseUrl(
  slug: string,
  opts: { platformDomain: string | undefined | null; currentOrigin: string; mode: 'subdomain' | 'path' },
): string {
  const domain = normalizeDomain(opts.platformDomain);
  if (opts.mode === 'subdomain') {
    return opts.currentOrigin.replace(/\/$/, '');
  }
  if (domain && opts.currentOrigin.endsWith(domain)) {
    return `https://${slug}.${domain}`;
  }
  return `${opts.currentOrigin.replace(/\/$/, '')}/s/${slug}`;
}

function hostOf(origin: string): { protocol: string; host: string; hostname: string } {
  try {
    const u = new URL(origin);
    return { protocol: u.protocol, host: u.host, hostname: u.hostname.toLowerCase() };
  } catch {
    return { protocol: 'https:', host: '', hostname: '' };
  }
}

/**
 * Абсолютный адрес любого сайта (точки или сети) + путь. path '' — адрес без слэша в конце
 * (как `${origin}${basename}`), '/' — со слэшем.
 * - поддомены на боевом домене → `https://<slug>.<домен><path>`;
 * - поддомены на `*.localhost:<порт>` (разработка) → `http://<slug>.localhost:<порт><path>`;
 * - режим пути (превью-хосты, тесты) → `<origin>/s/<slug><path>`.
 * canonical — адрес на боевом домене, где бы ни был открыт кабинет (QR-коды, сообщения);
 * без домена платформы — как обычно.
 */
export function siteUrl(
  slug: string,
  path: string,
  opts: {
    platformDomain: string | undefined | null;
    currentOrigin: string;
    mode: SiteMode;
    /** Хост текущей страницы; по умолчанию — из currentOrigin. */
    hostname?: string;
    canonical?: boolean;
  },
): string {
  const domain = normalizeDomain(opts.platformDomain);
  const origin = opts.currentOrigin.replace(/\/$/, '');
  const cur = hostOf(origin);
  const hostname = (opts.hostname ?? cur.hostname).toLowerCase();
  if (domain && opts.canonical) return `https://${slug}.${domain}${path}`;
  const onPlatform = domain !== null && (hostname === domain || hostname.endsWith(`.${domain}`));
  if (onPlatform) return `https://${slug}.${domain}${path}`;
  const onLocalhost = hostname === 'localhost' || hostname.endsWith('.localhost');
  if (opts.mode === 'subdomain' && onLocalhost) {
    const port = cur.host.includes(':') ? `:${cur.host.split(':').pop()}` : '';
    return `${cur.protocol}//${slug}.localhost${port}${path}`;
  }
  if (opts.mode === 'subdomain' && domain) return `https://${slug}.${domain}${path}`;
  return `${origin}/s/${slug}${path}`;
}
